/* Smart revision scan: compare the previous revision of a sheet with the new
   one and return the regions that changed, so the viewer can drop a classic
   scalloped "revision cloud" over each one.

   It works on the rendered images (the previews are same-size renders of the
   same sheet frame, which revisions almost always keep), grayscales both,
   thresholds per-pixel difference, then clusters changed cells into boxes.
   All boxes come back in normalised page space (0..1) so they overlay at any
   zoom. Best-effort: on any load failure it returns []. */

import type { DrawingCloud } from "./types";

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = () => rej(new Error("img"));
    img.src = src;
  });
}

function toGray(img: HTMLImageElement, w: number, h: number): Uint8ClampedArray | null {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  let data: Uint8ClampedArray;
  try { data = ctx.getImageData(0, 0, w, h).data; } catch { return null; }
  const g = new Uint8ClampedArray(w * h);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    // luma; unpainted alpha already flattened to white above
    g[p] = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) | 0;
  }
  return g;
}

const W = 1100;                 // working width (matches the viewer's page space)
const CELL = 14;                // grid cell in working px
const PIX_DIFF = 42;            // per-pixel grayscale delta that counts as "changed"
const CELL_FRAC = 0.06;         // fraction of a cell's pixels that must differ
const MIN_CELLS = 2;            // clusters smaller than this are noise
const PAD = 1;                  // grow each box by a cell so the cloud clears the change

/** Compare two rendered sheets. `oldSrc`/`newSrc` are image URLs or data URIs. */
export async function diffSheets(oldSrc: string, newSrc: string): Promise<DrawingCloud[]> {
  try {
    const [a, b] = await Promise.all([loadImage(oldSrc), loadImage(newSrc)]);
    const aspect = (b.naturalWidth / b.naturalHeight) || (a.naturalWidth / a.naturalHeight) || (W / 780);
    const h = Math.round(W / aspect);
    const ga = toGray(a, W, h), gb = toGray(b, W, h);
    if (!ga || !gb) return [];

    const cols = Math.ceil(W / CELL), rows = Math.ceil(h / CELL);
    const changed = new Uint8Array(cols * rows);
    for (let cy = 0; cy < rows; cy++) {
      for (let cx = 0; cx < cols; cx++) {
        const x0 = cx * CELL, y0 = cy * CELL, x1 = Math.min(W, x0 + CELL), y1 = Math.min(h, y0 + CELL);
        let diff = 0, total = 0;
        for (let y = y0; y < y1; y++) {
          const row = y * W;
          for (let x = x0; x < x1; x++) { total++; if (Math.abs(ga[row + x] - gb[row + x]) > PIX_DIFF) diff++; }
        }
        if (total && diff / total > CELL_FRAC) changed[cy * cols + cx] = 1;
      }
    }

    // flood-fill changed cells into connected components (8-connectivity)
    const seen = new Uint8Array(cols * rows);
    const boxes: { x0: number; y0: number; x1: number; y1: number; n: number }[] = [];
    const stack: number[] = [];
    for (let i = 0; i < changed.length; i++) {
      if (!changed[i] || seen[i]) continue;
      stack.length = 0; stack.push(i); seen[i] = 1;
      let minx = cols, miny = rows, maxx = 0, maxy = 0, n = 0;
      while (stack.length) {
        const c = stack.pop()!; const cx = c % cols, cy = (c / cols) | 0;
        n++; if (cx < minx) minx = cx; if (cy < miny) miny = cy; if (cx > maxx) maxx = cx; if (cy > maxy) maxy = cy;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue;
          const nx = cx + dx, ny = cy + dy;
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
          const ni = ny * cols + nx;
          if (changed[ni] && !seen[ni]) { seen[ni] = 1; stack.push(ni); }
        }
      }
      if (n >= MIN_CELLS) boxes.push({ x0: minx, y0: miny, x1: maxx, y1: maxy, n });
    }

    // pad, then merge any boxes that now overlap (keeps clouds from stacking)
    let rects = boxes.map((bx) => ({
      x0: Math.max(0, bx.x0 - PAD), y0: Math.max(0, bx.y0 - PAD),
      x1: Math.min(cols - 1, bx.x1 + PAD), y1: Math.min(rows - 1, bx.y1 + PAD),
    }));
    let merged = true;
    while (merged) {
      merged = false;
      outer: for (let i = 0; i < rects.length; i++) {
        for (let j = i + 1; j < rects.length; j++) {
          const A = rects[i], B = rects[j];
          if (A.x0 <= B.x1 + 1 && B.x0 <= A.x1 + 1 && A.y0 <= B.y1 + 1 && B.y0 <= A.y1 + 1) {
            rects[i] = { x0: Math.min(A.x0, B.x0), y0: Math.min(A.y0, B.y0), x1: Math.max(A.x1, B.x1), y1: Math.max(A.y1, B.y1) };
            rects.splice(j, 1); merged = true; break outer;
          }
        }
      }
    }
    if (rects.length > 40) rects = rects.slice(0, 40);      // a sane ceiling

    // → normalised page space
    return rects.map((r) => ({
      x: (r.x0 * CELL) / W,
      y: (r.y0 * CELL) / h,
      w: Math.min(1, ((r.x1 + 1) * CELL) / W) - (r.x0 * CELL) / W,
      h: Math.min(1, ((r.y1 + 1) * CELL) / h) - (r.y0 * CELL) / h,
    }));
  } catch { return []; }
}
